import { PriceInterface, VariationInterface } from "@/interface/business/item.interface";
import { PriceMoney } from "@/modules/location";
import { VariationType, ItemImageType } from "@/types/business/item.type";
import { Pricing } from "@/modules/ooc/pricing";
import { NAME_LOCAL_NAME_KEY, ITEM_PRICING_TYPE } from "@/enums/orders.enum";
import { $vm } from "@/components";
import store from "@/store";

/**
 * Variations Class
 * @param {VariationType} Variation
 */
export default class Variation implements VariationInterface {
  id: string = "";
  name: string = "";
  local_name: string = "";
  original_name?: string = "";
  original_local_name?: string = "";
  plu?: string = "";
  pricing_type: string = "";
  barcode?: string;
  sku?: string;
  image_url?: string = "";
  image?: ItemImageType;
  cost: number = 0;
  price_money: PriceInterface = new PriceMoney({ amount: 0 });
  selected: boolean = false;
  external_id?: string = "";
  price?: number = 0;
  isStaged: boolean = true;
  pricing: any;
  message: string = "";

  constructor(data: VariationType) {
    if (!data) {
      return;
    }
    this.id = data.id || "";
    this.name = data.name || "";
    this.local_name = data.local_name || "";
    this.original_name = data.original_name ?? data.name ?? "";
    this.original_local_name = data.original_local_name ?? data.local_name ?? "";
    this.plu = data.plu || "";
    this.image_url = data.image_url || "";
    this.pricing_type = data.pricing_type || "";
    this.barcode = data.barcode || "";
    this.sku = data.sku || "";
    this.image = data.image;
    this.selected = data.selected || false;
    this.cost = data.cost;
    this.isStaged = true;
    this.pricing = new Pricing(data.pricing) || null;
    this.setupType(data);
    this.setupPriceMoney(data);
  }

  /**
   * Setup Variation Price
   * @return {void}
   */
  setupPriceMoney(data) {
    let priceMoney = data.price_money;
    this.price = data.price;
    // price money from sale object
    if (data?.price) {
      priceMoney = {
        amount: data?.price,
      };
    }
    // If by this point data.price is not defined and there is a data.pricing.price assign to the amount because this
    // how the data comes from ooc
    if (!data?.price && data?.pricing?.price) {
      priceMoney = {
        amount: data?.pricing?.price,
      };
    }
    this.price_money = new PriceMoney(priceMoney);
  }

  /**
   * Setup Variation Price
   * @return {void}
   */
  setupType(data) {
    this.pricing_type = data?.type || data.pricing_type;
  }

  /**
   * Select item
   * @return {void}
   */
  public select(): void {
    this.selected = true;
  }

  /**
   * UnSelect item
   * @return {void}
   */
  public unSelect(): void {
    this.selected = false;
  }

  /**
   * Variation Price package
   * @return {void}
   */
  public pricePackage(): string {
    return this.price_money.getPricePackage();
  }

  /**
   * UnSelect item
   * @return {void}
   */
  public priceValue(): number {
    return this.price_money?.amount || 0;
  }

  /**
   * check if the variation is staged
   * @param stagedVariations
   */
  public checkIsStaged(stagedVariations: Array<Variation>): Boolean {
    return !!stagedVariations.find((item) => item.original_name === this.original_name);
  }

  /**
   * Validate variation
   * @return {boolean}
   */
  public validate(): boolean {
    this.message = "";
    if (this.pricing_type === ITEM_PRICING_TYPE.VARIABLE && !this.price_money.amount) {
      this.message = $vm.$t("price_validation");
      return false;
    }
    return true;
  }

  /**
   * Get variation display name
   * @return {string} name
   */
  public getDisplayName(): string {
    const { state } = <any>store;
    return this?.[NAME_LOCAL_NAME_KEY[state.App.lang]] || this.name;
  }
}
